// ===========================================
// Student Avatar Component
// ===========================================
// Displays student's custom avatar or default based on age

import { Avatar, type SxProps, type Theme } from '@mui/material';
import { getAgeGroupFromBirthDate, getCharacterColor, getCharacterName, type AgeGroup } from '../brand';

// Map character names to age groups for color lookup
const CHARACTER_TO_AGE_GROUP: Record<string, AgeGroup> = {
  alice: 'PRESCHOOL',
  steve: 'KIDS',
  liam: 'TEENS',
  floyd: 'ADULT',
};

interface StudentAvatarProps {
  avatar: string | null;
  birthDate: string;
  firstName: string;
  lastName: string;
  size?: number;
  onClick?: () => void;
  sx?: SxProps<Theme>;
}

/**
 * StudentAvatar component displays a student's selected avatar
 * or falls back to the age-appropriate character mascot
 */
export function StudentAvatar({
  avatar,
  birthDate,
  firstName,
  lastName,
  size = 36,
  onClick,
  sx,
}: StudentAvatarProps) {
  // Get default avatar based on age if no custom avatar selected
  const ageGroup = getAgeGroupFromBirthDate(birthDate);
  const defaultCharacter = getCharacterName(ageGroup).toLowerCase();
  const defaultAvatar = `${defaultCharacter}/${defaultCharacter}-happy.svg`;

  const displayAvatar = avatar || defaultAvatar;

  // Get color based on the displayed avatar's character (not age)
  const avatarCharacter = displayAvatar.split('/')[0];
  const avatarAgeGroup = CHARACTER_TO_AGE_GROUP[avatarCharacter] || ageGroup;
  const characterColor = getCharacterColor(avatarAgeGroup);
  const initials = `${firstName[0] || ''}${lastName[0] || ''}`;

  return (
    <Avatar
      src={`/avatars/${displayAvatar}`}
      alt={`${firstName} ${lastName}'s avatar`}
      onClick={onClick}
      sx={{
        width: size,
        height: size,
        bgcolor: characterColor,
        cursor: onClick ? 'pointer' : 'default',
        border: `2px solid ${characterColor}`,
        '& img': {
          objectFit: 'contain',
          p: 0.5,
          bgcolor: 'white',
          borderRadius: '50%',
        },
        '&:hover': onClick
          ? {
              transform: 'scale(1.05)',
              boxShadow: 2,
            }
          : undefined,
        transition: 'all 0.2s',
        ...sx,
      }}
    >
      {initials}
    </Avatar>
  );
}

export default StudentAvatar;
